/**
 * SlideStage - Reusable slide viewer component with filmstrip and player controls
 *
 * Encapsulates the slide viewing experience for use in both:
 * - VideoWorkspacePanels (Studio tab) - full featured with edit mode
 * - NarrationView - simplified for audio workflow
 *
 * Features:
 * - GsapSlidePreviewEnhanced for single slide viewing
 * - VideoCompositionPlayer for full video playback (optional)
 * - Filmstrip navigation (optional)
 * - Player controls (play/pause, timeline, time display)
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GsapSlidePreviewEnhanced, GsapSlidePreviewRef } from '@/components/slides/GsapSlidePreviewEnhanced';
import { VideoCompositionPlayer, type VideoCompositionPlayerRef } from './VideoCompositionPlayer';
import { SlideFilmstripItem } from './SlideFilmstripItem';
import { ScrollableFilmstrip } from './ScrollableFilmstrip';
import { SegmentedTimeline } from './SegmentedTimeline';
import { useVideoWorkspaceStore, type SlideGenerationStatus } from '@/stores/videoWorkspaceStore';
import { slidesAPI } from '@/services/api';
import type { VideoManifest, VideoPlaybackState as CompositionPlaybackState } from '@/types/videoManifest';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  RotateCcw,
  Layers,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

// Format time as M:SS or H:MM:SS
const formatTime = (seconds: number): string => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
};

interface SlideStageSlide {
  id: string;
  title: string;
  thumbnail?: string;
  duration?: number;
  status?: string;
  childConversationId?: string;
}

interface SlideStageProps {
  conversationId: string;
  currentSlideId: string | null;
  slides: SlideStageSlide[];
  videoManifest: VideoManifest | null;
  onSlideChange: (slideId: string) => void;

  // Display options
  showFilmstrip?: boolean;
  showPlayerControls?: boolean;
  className?: string;

  // Composition mode (video playback vs single slide)
  isCompositionMode?: boolean;
  onCompositionModeChange?: (mode: boolean) => void;

  // Optional callbacks for advanced features
  onSlideDataLoad?: (slideId: string, data: any) => void;
}

export const SlideStage: React.FC<SlideStageProps> = ({
  conversationId,
  currentSlideId,
  slides,
  videoManifest,
  onSlideChange,
  showFilmstrip = true,
  showPlayerControls = true,
  className,
  isCompositionMode = false,
  onCompositionModeChange: _onCompositionModeChange,
  onSlideDataLoad,
}) => {
  const previewRef = useRef<GsapSlidePreviewRef>(null);
  const compositionRef = useRef<VideoCompositionPlayerRef>(null);

  // Current slide data for preview
  const [currentSlideData, setCurrentSlideData] = useState<any>(null);
  const [isLoadingSlide, setIsLoadingSlide] = useState(false);
  // Track the effective conversation ID for the current slide (child conversation for video mode)
  const [effectiveConvId, setEffectiveConvId] = useState<string>(conversationId);

  // Player state
  const [playerState, setPlayerState] = useState({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
  });

  // Composition playback state
  const [compositionState, setCompositionState] = useState<CompositionPlaybackState>({
    isPlaying: false,
    currentTime: 0,
    totalDuration: 0,
    currentSlideId: '',
    currentSlideIndex: -1,
    currentSlideTime: 0,
    currentSlideDuration: 0,
    playbackRate: 1,
    isContinuousMode: true,
    isTransitioning: false,
  });

  // Filmstrip visibility
  const [filmstripVisible, setFilmstripVisible] = useState(showFilmstrip);

  // Get slide conversations from store for status
  const { slideConversations } = useVideoWorkspaceStore();

  // Get project resolution from narration context (defaults to 1920x1080)
  const projectResolution = useVideoWorkspaceStore((s) => s.getProjectResolution(conversationId));

  // Find current slide index
  const currentIndex = slides.findIndex(s => s.id === currentSlideId);

  // Effective player state (from composition or single slide)
  const effectivePlayerState = isCompositionMode
    ? {
        isPlaying: compositionState.isPlaying,
        currentTime: compositionState.currentTime,
        duration: compositionState.totalDuration,
      }
    : playerState;

  // Progress percentage
  const progress = effectivePlayerState.duration > 0
    ? (effectivePlayerState.currentTime / effectivePlayerState.duration) * 100
    : 0;

  // Load slide data when currentSlideId changes
  useEffect(() => {
    if (!currentSlideId || !conversationId) {
      setCurrentSlideData(null);
      return;
    }

    // In composition mode, VideoCompositionPlayer handles loading
    if (isCompositionMode) return;

    // Find the slide to get its child conversation ID (for video mode)
    const slideInfo = slides.find(s => s.id === currentSlideId);

    // In video mode (when slides have childConversationId), only load if:
    // 1. The slide has a childConversationId (has been generated)
    // 2. The slide status is 'completed'
    const isVideoModeSlide = slideInfo?.childConversationId;

    if (isVideoModeSlide && slideInfo?.status !== 'completed') {
      // Slide not ready yet (pending/generating/failed)
      console.log('[SlideStage] Slide not ready:', currentSlideId, 'status:', slideInfo?.status);
      setCurrentSlideData(null);
      setIsLoadingSlide(false);
      return;
    }

    // If it's a video mode slide but no child conversation ID, slide hasn't been generated
    if (slides.some(s => s.childConversationId) && !slideInfo?.childConversationId) {
      console.log('[SlideStage] Video mode slide without child conversation:', currentSlideId);
      setCurrentSlideData(null);
      setIsLoadingSlide(false);
      return;
    }

    let cancelled = false;

    const loadSlide = async () => {
      setIsLoadingSlide(true);
      try {
        // In video mode, each slide has a childConversationId where the slide data lives
        const effectiveConversationId = slideInfo?.childConversationId || conversationId;

        // For video mode with child conversations, use 'slide-001' as the identifier
        // since each child conversation has its own slide at that path
        const slideIdentifier = slideInfo?.childConversationId ? 'slide-001' : currentSlideId;

        console.log('[SlideStage] Loading slide:', {
          currentSlideId,
          effectiveConversationId,
          slideIdentifier,
          hasChildConv: !!slideInfo?.childConversationId,
          status: slideInfo?.status
        });

        // Store the effective conversation ID for use by GsapSlidePreviewEnhanced
        setEffectiveConvId(effectiveConversationId);

        const data = await slidesAPI.getGsapSlide(slideIdentifier, effectiveConversationId);
        if (!cancelled && data) {
          setCurrentSlideData(data);
          onSlideDataLoad?.(currentSlideId, data);
        }
      } catch (error) {
        console.error('[SlideStage] Failed to load slide:', error);
        if (!cancelled) {
          setCurrentSlideData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSlide(false);
        }
      }
    };

    loadSlide();

    return () => {
      cancelled = true;
    };
  }, [currentSlideId, conversationId, isCompositionMode, slides, onSlideDataLoad]);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (isCompositionMode) {
      compositionRef.current?.togglePlay();
    } else {
      previewRef.current?.togglePlay();
    }
  }, [isCompositionMode]);

  // Handle restart
  const handleRestart = useCallback(() => {
    if (isCompositionMode) {
      compositionRef.current?.seek(0);
    } else {
      previewRef.current?.restart();
    }
  }, [isCompositionMode]);

  // Handle slide click in filmstrip
  const handleSlideSelect = useCallback((slideId: string) => {
    onSlideChange(slideId);
    if (isCompositionMode && compositionRef.current) {
      compositionRef.current.goToSlide(slideId);
    }
  }, [onSlideChange, isCompositionMode]);

  // Prev/next move the composition too, the same as a filmstrip click
  const handlePrevSlide = useCallback(() => {
    if (currentIndex > 0) {
      handleSlideSelect(slides[currentIndex - 1].id);
    }
  }, [currentIndex, slides, handleSlideSelect]);

  const handleNextSlide = useCallback(() => {
    if (currentIndex < slides.length - 1) {
      handleSlideSelect(slides[currentIndex + 1].id);
    }
  }, [currentIndex, slides, handleSlideSelect]);

  // Handle timeline seek
  const handleTimelineSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const seekTime = percentage * effectivePlayerState.duration;

    if (isCompositionMode) {
      compositionRef.current?.seek(seekTime);
    } else {
      previewRef.current?.seek(seekTime);
    }
  }, [isCompositionMode, effectivePlayerState.duration]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Slide Viewer Area - with layoutId for shared element transitions */}
      <motion.div
        layoutId="slide-viewer"
        className="flex-1 min-h-0 relative"
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
          mass: 0.8,
        }}
      >
        {isCompositionMode && videoManifest ? (
          <VideoCompositionPlayer
            ref={compositionRef}
            manifest={videoManifest}
            conversationId={conversationId}
            onStateChange={setCompositionState}
            onSlideStart={(slideId) => {
              onSlideChange(slideId);
            }}
            className="w-full h-full"
          />
        ) : currentSlideData ? (
          <GsapSlidePreviewEnhanced
            ref={previewRef}
            slide={currentSlideData}
            conversationId={effectiveConvId}
            renderControls={false}
            onPlayerStateChange={(state) => setPlayerState(state)}
            resolution={projectResolution}
            className="w-full h-full"
          />
        ) : isLoadingSlide ? (
          <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-zinc-900">
            <div className="animate-pulse text-gray-400 dark:text-zinc-600 text-sm">
              Loading slide...
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-zinc-900">
            <div className="text-center">
              {(() => {
                if (slides.length === 0) {
                  return <span className="text-gray-400 dark:text-zinc-600 text-sm">No slides available</span>;
                }
                const slideInfo = currentSlideId ? slides.find(s => s.id === currentSlideId) : null;
                if (!slideInfo) {
                  return <span className="text-gray-400 dark:text-zinc-600 text-sm">Select a slide</span>;
                }
                if (slideInfo.status === 'generating') {
                  return (
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                      <span className="text-gray-400 dark:text-zinc-600 text-sm">Generating slide...</span>
                    </div>
                  );
                }
                if (slideInfo.status === 'pending') {
                  return <span className="text-gray-400 dark:text-zinc-600 text-sm">Slide pending generation</span>;
                }
                if (slideInfo.status === 'failed') {
                  return <span className="text-red-400 dark:text-red-500 text-sm">Slide generation failed</span>;
                }
                return <span className="text-gray-400 dark:text-zinc-600 text-sm">Unable to load slide</span>;
              })()}
            </div>
          </div>
        )}
      </motion.div>

      {/* Player Controls */}
      {showPlayerControls && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-zinc-800">
          <div className="h-12 flex items-center gap-3 px-4">
            <TooltipProvider delayDuration={300}>
              {/* Left: Player Controls */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleRestart}
                      className="w-8 h-8 text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-zinc-100"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Restart</TooltipContent>
                </Tooltip>

                {slides.length > 1 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handlePrevSlide}
                        disabled={currentIndex <= 0}
                        className="w-8 h-8 text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-zinc-100 disabled:opacity-30"
                      >
                        <SkipBack className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Previous</TooltipContent>
                  </Tooltip>
                )}

                <button
                  onClick={handlePlayPause}
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-primary text-primary-foreground shadow-sm active:scale-95 transition-all"
                >
                  {effectivePlayerState.isPlaying ? (
                    <Pause className="w-4 h-4 fill-current" />
                  ) : (
                    <Play className="w-4 h-4 fill-current ml-0.5" />
                  )}
                </button>

                {slides.length > 1 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleNextSlide}
                        disabled={currentIndex >= slides.length - 1}
                        className="w-8 h-8 text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-zinc-100 disabled:opacity-30"
                      >
                        <SkipForward className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Next</TooltipContent>
                  </Tooltip>
                )}
              </div>

              {/* Center: Time + Timeline */}
              <div className="flex-1 flex items-center gap-2">
                <span className="text-[11px] font-mono text-gray-500 dark:text-zinc-500 w-10 text-right flex-shrink-0">
                  {formatTime(effectivePlayerState.currentTime)}
                </span>

                {/* Timeline */}
                {isCompositionMode && videoManifest ? (
                  <div className="flex-1">
                    <SegmentedTimeline
                      manifest={videoManifest}
                      playbackState={compositionState}
                      onSeek={(time) => compositionRef.current?.seek(time)}
                      onSlideClick={(slideId) => compositionRef.current?.goToSlide(slideId)}
                      interactive={true}
                      showTransitions={false}
                    />
                  </div>
                ) : (
                  <div
                    className="flex-1 h-6 flex items-center group cursor-pointer"
                    onClick={handleTimelineSeek}
                  >
                    <div className="w-full h-1.5 bg-gray-200 dark:bg-zinc-800 rounded-full overflow-hidden relative group-hover:h-2 transition-all">
                      <div
                        className="h-full bg-primary rounded-full transition-[width]"
                        style={{ width: `${progress}%` }}
                      />
                      <div
                        className={cn(
                          "absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-md border-2 border-primary",
                          "opacity-0 group-hover:opacity-100 transition-opacity",
                          "pointer-events-none"
                        )}
                        style={{ left: `calc(${progress}% - 6px)` }}
                      />
                    </div>
                  </div>
                )}

                <span className="text-[11px] font-mono text-gray-500 dark:text-zinc-500 w-10 flex-shrink-0">
                  {formatTime(effectivePlayerState.duration)}
                </span>
              </div>

              {/* Right: Filmstrip Toggle */}
              {showFilmstrip && slides.length > 1 && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setFilmstripVisible(!filmstripVisible)}
                        className={cn(
                          "w-8 h-8 text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-zinc-100",
                          filmstripVisible && "bg-black/5 dark:bg-white/5"
                        )}
                      >
                        <Layers className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{filmstripVisible ? 'Hide' : 'Show'} timeline</TooltipContent>
                  </Tooltip>
                </div>
              )}
            </TooltipProvider>
          </div>
        </div>
      )}

      {/* Filmstrip */}
      {showFilmstrip && slides.length > 1 && (
        <div
          className={cn(
            "flex-shrink-0 border-t border-gray-200 dark:border-zinc-800",
            "transition-all duration-300 ease-out",
            filmstripVisible ? "opacity-100" : "opacity-0 h-0 overflow-hidden"
          )}
        >
          <ScrollableFilmstrip
            isVisible={filmstripVisible}
            height="h-[76px]"
            gap="gap-2"
            paddingX="px-4"
          >
            {slides.map((slide, index) => {
              const slideConv = slideConversations.find(sc => sc.outlineSlideId === slide.id);
              const status = (slideConv?.status || slide.status || 'completed') as SlideGenerationStatus;
              // Use child conversation ID for video mode, parent for regular mode
              const slideConvId = slide.childConversationId || conversationId;

              return (
                <SlideFilmstripItem
                  key={slide.id}
                  id={slide.id}
                  title={slide.title}
                  index={index}
                  thumbnail={slide.thumbnail}
                  duration={slide.duration}
                  status={status}
                  isActive={slide.id === currentSlideId}
                  onClick={() => handleSlideSelect(slide.id)}
                  conversationId={slideConvId}
                />
              );
            })}
          </ScrollableFilmstrip>
        </div>
      )}
    </div>
  );
};

export default SlideStage;
